import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Text } from '@mantine/core';
import { IconCheck, IconLock } from '@tabler/icons-react';
import HomeSection from '@/components/app/home/HomeSection';
import { useOnboarding, type OnboardingStep } from '@/hooks/useOnboarding';
import classes from './home.module.css';

// Онбординг на «Главной»: слева «Первые шаги» — список обязательных данных с прогрессом, аттестация
// последняя и закрыта до заполнения данных; справа «Следующий шаг» — что сделать сейчас. На телефоне
// «Следующий шаг» стоит сверху. Самоскрывается, когда онбординг не активен (не менеджер, агент,
// организация аттестована, данные не загружены). См. docs/pages/home/first-steps-onboarding.md.
export default function FirstStepsCard() {
  const { t } = useTranslation();
  const { active, steps, doneCount, totalCount } = useOnboarding();

  if (!active) {
    return null;
  }

  const progress = t('app.home.firstSteps.progress', { done: doneCount, total: totalCount });
  // Следующий шаг — первый незаполненный и открытый. Закрытая аттестация следующим шагом не бывает.
  const next = steps.find((step) => !step.done && !step.locked);

  return (
    <div className={`${classes.columns} ${classes.nextFirst}`}>
      <HomeSection
        title={
          <>
            {t('app.home.firstSteps.title')}{' '}
            <Text span size="sm" fw={500} c="gray.7">
              {progress}
            </Text>
          </>
        }
      >
        <div className={classes.segments} role="img" aria-label={progress}>
          {steps.map((step) => (
            <i key={step.key} className={`${classes.segment}${step.done ? ` ${classes.segmentDone}` : ''}`} />
          ))}
        </div>
        {steps.map((step, index) => (
          <StepRow
            key={step.key}
            step={step}
            number={index + 1}
            current={step === next}
            label={t(`app.home.firstSteps.step.${step.key}.label`)}
          />
        ))}
      </HomeSection>

      {next ? (
        <HomeSection title={t('app.home.firstSteps.next')}>
          <div className={classes.next}>
            <Text fw={600} fz={18}>
              {t(`app.home.firstSteps.step.${next.key}.label`)}
            </Text>
            <Button component={Link} to={next.to} mt={14}>
              {t(`app.home.firstSteps.step.${next.key}.cta`)}
            </Button>
          </div>
        </HomeSection>
      ) : null}
    </div>
  );
}

interface StepRowProps {
  readonly step: OnboardingStep;
  readonly number: number;
  readonly current: boolean;
  readonly label: string;
}

function StepRow({ step, number, current, label }: StepRowProps) {
  const { t } = useTranslation();

  if (step.done) {
    return (
      <div className={classes.step}>
        <IconCheck size={18} color="var(--mantine-color-brand-7)" aria-hidden />
        <Text c="gray.7">{label}</Text>
        <Text size="sm" fw={600} c="brand.7">
          {t('app.home.firstSteps.done')}
        </Text>
      </div>
    );
  }

  if (step.locked) {
    return (
      <div className={classes.step}>
        <IconLock size={18} color="var(--mantine-color-gray-7)" aria-hidden />
        <Text c="gray.7">{label}</Text>
        <Text size="sm" c="gray.7" className={classes.stepNote}>
          {t('app.home.firstSteps.attestationLocked')}
        </Text>
      </div>
    );
  }

  return (
    <Link to={step.to} className={classes.step} aria-current={current ? 'step' : undefined}>
      <Text size="sm" c="gray.7" ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {number}
      </Text>
      <Text fw={current ? 600 : 500}>{label}</Text>
      <span className={classes.stepGo}>{t(`app.home.firstSteps.step.${step.key}.cta`)}</span>
    </Link>
  );
}
